import React, { useState, useEffect } from "react";
import { UnifiedModal } from "@/components/ui/unified-modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/Pill";
import { TagLabel } from "@/components/TagLabel";
import { Loader2, Check, Pencil } from "lucide-react";
import { TAG_COLORS, type Tag, type TagColor } from "@/services/tags.service";
import { cn } from "@/lib/utils";
import { ERROR, ROW_TITLE } from "@/lib/typography";

interface TagEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tag: Tag;
  allTags: Tag[];
  onSave: (tagId: string, name: string, color: TagColor) => Promise<void>;
  onDelete: (tag: Tag) => void;
}

export function TagEditDialog({ open, onOpenChange, tag, allTags, onSave, onDelete }: TagEditDialogProps) {
  const [name, setName] = useState(tag.name);
  const [selectedColor, setSelectedColor] = useState<TagColor>(tag.color);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Reset when tag changes
  useEffect(() => {
    setName(tag.name);
    setSelectedColor(tag.color);
    setError("");
  }, [tag]);

  const handleSave = async () => {
    const trimmedName = name.trim();

    // Validation
    if (!trimmedName) {
      setError("Tag name cannot be empty");
      return;
    }

    if (trimmedName.length > 50) {
      setError("Tag name must be 50 characters or less");
      return;
    }

    // Check for duplicates (case-insensitive, excluding current tag)
    const isDuplicate = allTags.some(t => t.id !== tag.id && t.name.toLowerCase() === trimmedName.toLowerCase());

    if (isDuplicate) {
      setError("A tag with this name already exists");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await onSave(tag.id, trimmedName, selectedColor);
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || "Failed to save tag");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    onDelete(tag);
    onOpenChange(false);
  };

  return (
    <UnifiedModal
      open={open}
      onOpenChange={onOpenChange}
      icon={<Pencil />}
      title={
        <span className="flex flex-wrap items-center gap-2">
          <span>Edit Tag</span>
          <Pill
            variant="outlined"
            className="border"
            style={{
              backgroundColor: selectedColor + "20",
              borderColor: selectedColor,
              color: selectedColor,
            }}
          >
            <TagLabel name={name || "Tag name"} color={selectedColor} size="xs" />
          </Pill>
        </span>
      }
      description="Update the name and color of your tag."
      stacked
      contentClassName="max-w-[90vw] sm:max-w-md"
    >
        <div className="space-y-4 pt-2">

          {/* Name Input */}
          <div className="space-y-2">
            <Input
              id="tag-name"
              value={name}
              onChange={e => {
                setName(e.target.value);
                setError("");
              }}
              placeholder="tag title"
              maxLength={50}
              autoFocus
            />
            {error && <p className={ERROR}>{error}</p>}
          </div>

          {/* Color Picker */}
          <div className="space-y-3">
            <label className={cn(ROW_TITLE, "text-foreground")}>Select a color</label>
            <div className="grid grid-cols-6 gap-2.5 justify-items-center">
              {TAG_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  className={cn(
                    "relative h-10 w-10 rounded-full",
                    "",
                    selectedColor === color && "ring-2 ring-offset-2 ring-offset-background",
                  )}
                  style={{
                    backgroundColor: color,
                    width: "40px",
                    height: "40px",
                    minWidth: "40px",
                    minHeight: "40px",
                    ...(selectedColor === color && {
                      ringColor: color,
                    }),
                  }}
                  aria-label={`Select color ${color}`}
                >
                  {selectedColor === color && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Check className="h-4 w-4 text-white drop-" strokeWidth={3} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-row gap-3 mt-2">
            <Button
              variant="outline"
              size="modal"
              onClick={handleDelete}
              disabled={saving}
              className="flex-1 border-2 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive dark:border-destructive dark:text-destructive dark:hover:bg-destructive/15"
            >
              Delete
            </Button>
            <Button
              size="modal"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="flex-1"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
      </div>
    </UnifiedModal>
  );
}
